import { useState } from "react";
import { Pencil, Eye, EyeOff, RotateCcw, Plus, Trash2, Megaphone, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const L = (lang, zh, en) => (lang === "zh" ? zh : en);

/** 2026-06 · Admin controls on a help section: 更新 / 显示-隐藏 / 恢复默认. */
export function SectionAdminBar({ section, lang, onSaved }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(section.title);
  const [body, setBody] = useState((section.bullets || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const isCustom = Boolean(section.customId);
  const base = isCustom ? `/help/content/custom/${section.customId}` : `/help/content/section/${section.id}`;

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      await api.put(base, {
        lang,
        title: title.trim(),
        bullets: body.split("\n").map((x) => x.trim()).filter(Boolean),
        ...extra,
      });
      toast.success(L(lang, "帮助内容已更新", "Help content updated"));
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const toggleHidden = async () => {
    try {
      await api.put(base, { lang, hidden: !section.hidden });
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reset = async () => {
    try {
      await api.delete(isCustom ? base : `/help/content/section/${section.id}?lang=${lang}`);
      toast.success(L(lang, isCustom ? "板块已删除" : "已恢复默认", isCustom ? "Section deleted" : "Default restored"));
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <span className="ml-auto flex items-center gap-1" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      {section.hidden && (
        <span className="text-[10px] font-semibold text-amber-600 px-1.5 py-0.5 rounded bg-amber-50"
          data-testid={`help-hidden-badge-${section.id}`}>{L(lang, "已隐藏", "Hidden")}</span>
      )}
      <button type="button" onClick={() => setOpen(true)} title={L(lang, "更新内容", "Update")}
        data-testid={`help-edit-${section.id}`}
        className="p-1.5 rounded-md text-muted-foreground hover:text-brand hover:bg-brand/10 transition-colors">
        <Pencil size={13} />
      </button>
      <button type="button" onClick={toggleHidden} title={L(lang, "显示 / 隐藏", "Show / hide")}
        data-testid={`help-toggle-${section.id}`}
        className="p-1.5 rounded-md text-muted-foreground hover:text-brand hover:bg-brand/10 transition-colors">
        {section.hidden ? <EyeOff size={13} /> : <Eye size={13} />}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="help-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{L(lang, "更新帮助内容", "Update help content")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={L(lang, "板块标题", "Section title")} className="h-9" data-testid="help-edit-title" />
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12}
              placeholder={L(lang, "每行一条要点，支持 **加粗** 与 `代码`", "One bullet per line — **bold** and `code` supported")}
              className="text-sm font-mono" data-testid="help-edit-body" />
            <p className="text-[11px] text-muted-foreground">
              {L(lang, "只修改当前界面语言的版本，另一语言保留默认文案。", "Only the current UI language is edited; the other language keeps its default.")}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={reset} data-testid="help-edit-reset"
              className="text-destructive hover:bg-destructive/10">
              {isCustom ? <Trash2 size={14} /> : <RotateCcw size={14} />}
              {L(lang, isCustom ? "删除板块" : "恢复默认", isCustom ? "Delete section" : "Restore default")}
            </Button>
            <Button onClick={() => save()} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="help-edit-save">
              <Save size={14} /> {L(lang, "保存", "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </span>
  );
}

/** 2026-06 · 「最新更新」button + panel. Entries are code-maintained by the dev
 *  team; each user's red dot clears once they open it. */
export function HelpNewsButton({ news, lang, onSeen }) {
  const [open, setOpen] = useState(false);
  const items = news?.items || [];
  const unread = news?.unread || 0;

  const openPanel = async () => {
    setOpen(true);
    if (unread > 0) {
      try {
        await api.post("/help/content/news/seen");
        onSeen?.();
      } catch { /* red dot just stays */ }
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openPanel}
        data-testid="help-news-button"
        className="relative inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-brand/50 bg-white text-brand text-xs font-semibold hover:bg-brand/10 transition-colors shrink-0"
      >
        <Megaphone size={13} />
        {L(lang, "最新更新", "What's new")}
        {unread > 0 && (
          <span data-testid="help-news-unread"
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="help-news-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Megaphone size={16} className="text-brand" />
              {L(lang, "最新更新", "What's new")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
            {items.length === 0 && (
              <p className="text-sm text-muted-foreground">{L(lang, "暂无更新内容", "No updates yet")}</p>
            )}
            {items.map((it, i) => (
              <section key={`${it.date}-${i}`} data-testid={`help-news-item-${i}`}
                className="border-l-2 border-brand/40 pl-4">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-[11px] font-mono text-muted-foreground">{it.date}</span>
                  <h3 className="font-semibold text-sm">{it.title}</h3>
                </div>
                <ul className="list-disc list-outside pl-5 mt-2 space-y-1.5 text-sm leading-relaxed">
                  {(it.bullets || []).map((b, k) => (
                    <li key={`${i}-${k}`}>{renderNewsText(b)}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** minimal **bold** / `code` renderer — never touches innerHTML. */
function renderNewsText(text) {
  return String(text || "").split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(part)) return <code key={i} className="px-1 py-0.5 rounded bg-muted font-mono text-[12px]">{part.slice(1, -1)}</code>;
    return part;
  });
}

/** 2026-06 · 新增板块 button (admin). */
export function AddSectionButton({ lang, onSaved }) {
  const add = async () => {
    try {
      const { data } = await api.post("/help/content/custom", {
        lang, title: L(lang, "新板块", "New section"), bullets: [L(lang, "在这里填写内容", "Write the content here")],
      });
      toast.success(L(lang, "板块已新增，点铅笔编辑内容", "Section added — click the pencil to edit"));
      onSaved(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Button variant="outline" size="sm" onClick={add} data-testid="help-add-section" className="h-9 rounded-md">
      <Plus size={14} /> {L(lang, "新增板块", "Add section")}
    </Button>
  );
}
